import CircularProgress from '@mui/material/CircularProgress'
import React, { FC, Suspense } from 'react'

import { Layout } from '@/common/components'
import type { RouteResponse } from '@/core/router'
import { UserRoles } from '@/features/auth'
import { useHistory } from '@/hooks/useHistory'
import { type RootState, useTypedSelector } from '@/store'

type PrivateRouteProps = {
  route: RouteResponse
  roles: UserRoles[]
  tenantId: string
  handleChangeTheme: () => void
}

const userIsAuthorize = (authorize: string[], roles: string[]) => roles.some((role) => authorize?.includes(role))

export const PrivateRoute: FC<PrivateRouteProps> = ({ route, roles = [], handleChangeTheme }) => {
  const history = useHistory()
  const tenant = useTypedSelector((state: RootState) => state.authSlice.tenant)
  const RouteComponent = route.component
  const isParentTenant = tenant.is_parent === true
  const tenantStatusKnown = !route.requireParentTenant || Boolean(tenant.id)
  const userRoles = roles.filter((role) => Object.values(UserRoles).includes(role as UserRoles))
  const hasRoleAccess = userIsAuthorize(route.authorize, userRoles)
  const hasTenantAccess = !route.requireParentTenant || isParentTenant

  if (!tenantStatusKnown) {
    return <CircularProgress />
  }

  if (!hasRoleAccess || !hasTenantAccess) {
    history.push('/')
    return null
  }

  if (!RouteComponent) {
    console.error('PrivateRoute: route is missing a component', route)
    return null
  }

  return (
    <Suspense fallback={<CircularProgress />}>
      <Layout
        onChangeTheme={handleChangeTheme}
        standalone={route.standalone}
        flushTop={route.flushTop}
        wideGutters={route.wideGutters}>
        {React.createElement(RouteComponent, route.props)}
      </Layout>
    </Suspense>
  )
}
